import { audio } from '../systems/audio/AudioSystem.js';
import { COSMETIC_CATEGORIES, COSMETIC_CATEGORY_LABELS, cosmeticsByCategory, cosmeticMilestoneMet } from '../data/cosmetics.js';
import { PLAYER_SPRITES, HAT_SPRITES } from '../data/sprites.js';
import { drawSprite } from '../core/Sprite.js';

// A fixed reference body for skin previews — skins are universal, so any
// character represents them; this keeps every skin swatch consistent.
const PREVIEW_CHARACTER = 'vess';

/**
 * Shop page: the Wardrobe — spends Glimmer (the cosmetic currency) on
 * cosmetics and equips them. Mirrors ShopShardsMenu's structure (one-time
 * buys from a data map) plus a category tab strip like ShopSummonMenu's page
 * nav, and the pet buy/equip-toggle-then-rerender loop (ShopSummonMenu
 * _showPetDetails). Purely visual — see data/cosmetics.js.
 */
export class ShopCosmeticMenu {
  constructor(root, save, { onBack }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.category = COSMETIC_CATEGORIES[0]; // 'skin'
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay shop-detail-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _render() {
    // Preserve scroll position across the re-render (same as the other shop
    // pages — see ShopShardsMenu._render).
    const scrollPosition = this.el.querySelector('.shop-rows')?.scrollTop ?? 0;
    this.el.innerHTML = '';

    const kicker = document.createElement('div');
    kicker.className = 'menu-kicker';
    kicker.textContent = 'VANITY & FLAIR';
    this.el.appendChild(kicker);
    const title = document.createElement('div');
    title.className = 'menu-title';
    title.textContent = 'WARDROBE';
    this.el.appendChild(title);

    const glimmer = document.createElement('div');
    glimmer.className = 'menu-echoes';
    glimmer.innerHTML = `Glimmer: <span class="menu-glimmer-value">${this.save.glimmer}</span>`;
    this.el.appendChild(glimmer);

    const nav = document.createElement('div');
    nav.className = 'summon-page-nav';
    for (const cat of COSMETIC_CATEGORIES) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `menu-button menu-button-small ${this.category === cat ? 'menu-button-selected' : ''}`;
      button.textContent = COSMETIC_CATEGORY_LABELS[cat];
      button.addEventListener('click', () => { this.category = cat; audio.playSfx('click'); this._render(); });
      nav.appendChild(button);
    }
    this.el.appendChild(nav);

    const rows = document.createElement('div');
    rows.className = 'shop-rows';
    for (const def of cosmeticsByCategory(this.category)) rows.appendChild(this._buildRow(def));
    this.el.appendChild(rows);

    const hint = document.createElement('div');
    hint.className = 'scroll-hint';
    hint.textContent = '▼ Scroll to browse';
    this.el.appendChild(hint);

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);

    rows.scrollTop = scrollPosition;
  }

  _buildRow(def) {
    const owned = this.save.ownsCosmetic(def.id);
    const equipped = this.save.getEquippedCosmetic(def.category) === def.id;

    const row = document.createElement('div');
    row.className = `shop-row cosmetic-row${equipped ? ' cosmetic-row-equipped' : ''}`;
    row.appendChild(this._preview(def));

    const info = document.createElement('div');
    info.className = 'shop-row-info';
    info.innerHTML = `
      <div><span class="shop-row-name">${def.name}</span></div>
      <div class="shop-row-desc">${def.description}</div>
    `;
    row.appendChild(info);

    row.appendChild(this._buildActionButton(def, owned, equipped));
    return row;
  }

  // Preview: skins render the reference body recolored; hats render the hat
  // sprite; trails/fx render a color swatch.
  _preview(def) {
    if (def.category === 'skin' || def.category === 'hat') {
      const canvas = document.createElement('canvas');
      canvas.className = 'cosmetic-preview';
      canvas.width = 24;
      canvas.height = 24;
      const ctx = canvas.getContext('2d');
      if (def.category === 'skin') {
        drawSprite(ctx, PLAYER_SPRITES[PREVIEW_CHARACTER], 12, 12, { paletteOverride: def.palette });
      } else {
        drawSprite(ctx, HAT_SPRITES[def.spriteKey], 12, 12);
      }
      return canvas;
    }
    // trail / fx — a color swatch.
    const swatch = document.createElement('div');
    swatch.className = 'cosmetic-preview cosmetic-swatch';
    swatch.style.background = def.color;
    return swatch;
  }

  _buildActionButton(def, owned, equipped) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'menu-button menu-button-small';

    if (owned) {
      // Owned: toggle equip/unequip in this category's single slot.
      button.textContent = equipped ? 'Unequip' : 'Equip';
      button.addEventListener('click', () => {
        if (equipped) this.save.unequipCosmetic(def.category);
        else this.save.equipCosmetic(def.category, def.id);
        audio.playSfx('click');
        this._render();
      });
      return button;
    }

    if (def.milestone) {
      // Milestone-gated: claimable once the stat threshold is met (normally
      // auto-granted at run-end, but claimable here too if met mid-session),
      // otherwise a locked hint.
      if (cosmeticMilestoneMet(def, this.save.statistics)) {
        button.textContent = 'Claim';
        button.addEventListener('click', () => {
          this.save.unlockCosmetic(def.id);
          audio.playSfx('levelup');
          this._render();
        });
      } else {
        button.textContent = def.milestone.text;
        button.disabled = true;
        button.classList.add('cosmetic-locked');
      }
      return button;
    }

    if (def.memento) {
      // Reprise memento (R5): not bought or stat-gated — earned by helping a
      // season's world be rebuilt and the King felled. Unowned = a locked
      // hint; owned falls through the `owned` branch above and is equippable.
      button.textContent = 'Earned in Reprise';
      button.disabled = true;
      button.classList.add('cosmetic-locked');
      return button;
    }

    // Buyable with Glimmer.
    button.textContent = `Buy — ${def.cost}`;
    button.disabled = this.save.glimmer < def.cost;
    button.addEventListener('click', () => {
      if (this.save.spendGlimmer(def.cost)) {
        this.save.unlockCosmetic(def.id);
        this.save.equipCosmetic(def.category, def.id); // auto-equip on purchase, like trying it on
        audio.playSfx('levelup');
        this._render();
      }
    });
    return button;
  }

  destroy() {
    this.el.remove();
  }
}
